import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ItineraryCard } from '../components/ItineraryCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Seo } from '../components/Seo';
import { personSchema, breadcrumb } from '../lib/structuredData';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { BadgeCheck, MapPin, Users, Instagram, Youtube, Globe, Heart, Eye, Compass } from 'lucide-react';

const platformIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('instagram')) return Instagram;
  if (n.includes('youtube')) return Youtube;
  return Globe;
};

export default function CreatorProfile() {
  const { username } = useParams();
  const { currency } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.publicCreator(username).then(({ data }) => { setData(data); }).catch(() => {});
  }, [username]);

  if (!data) return (
    <Layout><div className="max-w-5xl mx-auto px-4 py-10 space-y-4"><Skeleton className="h-40 rounded-2xl" /><div className="grid gap-4 sm:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48 rounded-2xl" />)}</div></div></Layout>
  );

  const c = data.creator;
  const platforms = (c.platforms || []).filter((p) => p.url || p.platform);
  const totalViews = c.stats?.views || 0;
  const creatorName = c.display_name || c.name;
  const creatorDesc = (c.bio || `${creatorName} shares ${data.itineraries.length} hand-crafted travel itineraries and guides${c.niche ? ` in ${c.niche}` : ''} on FlightDeals.in.`).slice(0, 300);
  const creatorImg = c.profile_photo ? api.mediaUrl(c.profile_photo) : undefined;
  const creatorJsonLd = [
    personSchema({
      name: creatorName,
      alternateName: `@${c.username}`,
      image: creatorImg,
      description: creatorDesc,
      path: `/creators/${c.username}`,
      sameAs: platforms.map((p) => p.url).filter(Boolean),
    }),
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Creators', path: '/creators' },
      { name: creatorName },
    ]),
  ];
  const follow = async () => { try { const { data } = await api.followCreator(c.creator_id); toast.success(data.following ? 'Following!' : 'Unfollowed'); } catch { toast.error('Sign in to follow'); } };
  const scrollItineraries = () => document.getElementById('creator-itineraries')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <Layout>
      <Seo title={`${creatorName} (@${c.username})`} description={creatorDesc}
        path={`/creators/${c.username}`} image={creatorImg} type="profile" jsonLd={creatorJsonLd} />
      <section className="relative bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-20"><img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1600&q=80" alt="" className="w-full h-full object-cover" /></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <span className="grid place-items-center h-24 w-24 rounded-2xl bg-accent text-accent-foreground text-4xl font-semibold overflow-hidden shrink-0">{c.profile_photo ? <img src={api.mediaUrl(c.profile_photo)} alt={c.name} className="h-full w-full object-cover" data-testid="profile-photo" /> : (c.name||'C').charAt(0)}</span>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-3xl font-semibold flex items-center gap-2">{c.display_name || c.name} {c.verification_status === 'verified' && <BadgeCheck className="h-6 w-6 text-accent" data-testid="verified-badge" />}</h1>
              <p className="text-primary-foreground/70">@{c.username}{c.niche ? ` · ${c.niche}` : ''}</p>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-primary-foreground/85">
                <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" /> {(c.follower_count||0).toLocaleString()} followers</span>
                <span className="inline-flex items-center gap-1"><Eye className="h-4 w-4" /> {totalViews.toLocaleString()} views</span>
                <span className="inline-flex items-center gap-1"><Compass className="h-4 w-4" /> {data.itineraries.length} itineraries</span>
                {(c.location || c.country) && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {c.location || c.country}</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(c.creator_type||[]).map((t) => <Badge key={t} variant="outline" className="rounded-full text-[10px] border-primary-foreground/30 text-primary-foreground">{t}</Badge>)}
                {(c.niches||[]).slice(0, 4).map((n) => <Badge key={n} className="rounded-full text-[10px] bg-accent/20 text-accent">{n}</Badge>)}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={follow} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl" data-testid="follow-creator-button"><Heart className="h-4 w-4 mr-1.5" /> Follow</Button>
              <Button onClick={scrollItineraries} variant="outline" className="rounded-xl bg-background/10 border-primary-foreground/30 text-primary-foreground hover:bg-background/20" data-testid="explore-itineraries-cta">Explore My Itineraries</Button>
            </div>
          </div>
          {c.bio && <p className="mt-5 max-w-2xl text-primary-foreground/85">{c.bio}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {platforms.map((p, i) => { const Ic = platformIcon(p.platform); return (
              <a key={i} href={p.url || '#'} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary-foreground/75 hover:text-accent" data-testid={`social-${i}`}><Ic className="h-4 w-4" /> {p.platform}</a>
            ); })}
            {platforms.length === 0 && <>
              {c.instagram && <a href={c.instagram.startsWith('http') ? c.instagram : `https://instagram.com/${c.instagram}`} target="_blank" rel="noreferrer" className="text-primary-foreground/75 hover:text-accent"><Instagram className="h-5 w-5" /></a>}
              {c.youtube && <a href={c.youtube} target="_blank" rel="noreferrer" className="text-primary-foreground/75 hover:text-accent"><Youtube className="h-5 w-5" /></a>}
              {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-primary-foreground/75 hover:text-accent"><Globe className="h-5 w-5" /></a>}
            </>}
          </div>
        </div>
      </section>
      <section id="creator-itineraries" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="font-display text-xl font-semibold mb-4">Published itineraries ({data.itineraries.length})</h2>
        {data.itineraries.length === 0 ? <p className="text-muted-foreground">No published itineraries yet.</p> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="creator-itineraries-grid">{data.itineraries.map((it) => <ItineraryCard key={it.itinerary_id} it={it} currency={currency} />)}</div>
        )}
      </section>
    </Layout>
  );
}
